import { fonts } from '@/constants/fonts';
import { useThemeColor } from '@/hooks/useThemeColor';
import { Ionicons } from '@expo/vector-icons';
import React, { ReactNode, useEffect } from 'react';
import {
  Modal as RNModal,
  StyleSheet,
  TouchableOpacity,
  View,
  ScrollView,
  Dimensions,
  Platform,
  StatusBar,
  Pressable,
  type ViewStyle,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import ThemedText from '../core/ThemedText';

type ModalSize = 'sm' | 'md' | 'lg' | 'full';
type ModalPosition = 'center' | 'top' | 'bottom';

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: ModalSize;
  position?: ModalPosition;
  showCloseButton?: boolean;
  closeOnOverlayPress?: boolean;
  footer?: ReactNode;
  scrollable?: boolean;
  style?: ViewStyle;
}

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const SIZE_CONFIG = {
  sm: { width: SCREEN_WIDTH * 0.7, maxHeight: '60%' },
  md: { width: SCREEN_WIDTH * 0.85, maxHeight: '70%' },
  lg: { width: SCREEN_WIDTH * 0.95, maxHeight: '85%' },
  full: { width: SCREEN_WIDTH, maxHeight: '100%' },
} as const;

export default function Modal({
  visible,
  onClose,
  title,
  children,
  size = 'md',
  position = 'center',
  showCloseButton = true,
  closeOnOverlayPress = true,
  footer,
  scrollable = true,
  style,
}: ModalProps) {
  const backgroundColor = useThemeColor({}, 'background');
  const textColor = useThemeColor({}, 'text');
  const borderColor = useThemeColor({}, 'border');

  const opacity = useSharedValue(0);
  const scale = useSharedValue(0.9);
  const translateY = useSharedValue(position === 'bottom' ? 100 : position === 'top' ? -100 : 0);

  useEffect(() => {
    if (visible) {
      opacity.value = withTiming(1, { duration: 200 });
      scale.value = withSpring(1, { damping: 15, stiffness: 150 });
      translateY.value = withSpring(0, { damping: 15, stiffness: 150 });
    } else {
      opacity.value = withTiming(0, { duration: 150 });
      scale.value = withTiming(0.9, { duration: 150 });
      translateY.value = withTiming(
        position === 'bottom' ? 100 : position === 'top' ? -100 : 0,
        { duration: 150 }
      );
    }
  }, [visible, position]);

  const overlayStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  const modalStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [
      { scale: scale.value },
      { translateY: translateY.value },
    ],
  }));

  const sizeConfig = SIZE_CONFIG[size];

  const contentStyle: ViewStyle = {
    backgroundColor,
    borderRadius: size === 'full' ? 0 : 16,
    width: sizeConfig.width,
    maxHeight: sizeConfig.maxHeight,
    ...style,
  };

  const Content = scrollable ? ScrollView : View;

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="none"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        {/* Overlay */}
        <Animated.View style={[styles.overlay, overlayStyle]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={closeOnOverlayPress ? onClose : undefined}
          />
        </Animated.View>

        {/* Modal Content */}
        <Animated.View
          style={[
            styles.modal,
            contentStyle,
            position === 'top' && styles.modalTop,
            position === 'bottom' && styles.modalBottom,
            modalStyle,
          ]}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <View style={[styles.header, { borderBottomColor: borderColor }]}>
              {title && (
                <ThemedText style={styles.title}>{title}</ThemedText>
              )}
              {showCloseButton && (
                <TouchableOpacity
                  onPress={onClose}
                  style={styles.closeButton}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Ionicons name="close" size={24} color={textColor} />
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Body */}
          <Content
            style={styles.content}
            showsVerticalScrollIndicator={scrollable}
            bounces={scrollable}
          >
            {children}
          </Content>

          {/* Footer */}
          {footer && (
            <View style={[styles.footer, { borderTopColor: borderColor }]}>
              {footer}
            </View>
          )}
        </Animated.View>
      </View>
    </RNModal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: Platform.OS === 'android' ? StatusBar.currentHeight || 0 : 0,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modal: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  modalTop: {
    position: 'absolute',
    top: 20,
  },
  modalBottom: {
    position: 'absolute',
    bottom: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
  },
  title: {
    fontSize: 20,
    fontFamily: fonts.bold,
    flex: 1,
  },
  closeButton: {
    marginLeft: 16,
  },
  content: {
    padding: 20,
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
});
